import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronRight, GraduationCap, Search } from 'lucide-react';
import * as TeamsModule from 'modules/teams';
import { usePermission } from 'modules/auth/hooks';
import { PERMISSIONS } from 'modules/auth/permissions';
import { CreateTeamModal } from 'pages/Teams/CreateTeamModal';
import { GroupTile } from 'pages/Teams/GroupTile';
import { useSearchParamState } from 'hooks/use-search-param-state';
import {
  MobileScreen,
  ScreenHeader,
  HeaderPlus,
  ScreenBody,
  SearchInput,
  ListCard,
  MobileSkeleton,
  MobileEmpty,
  MobileError,
  M,
} from 'components/mobile';

/** One card of group rows under a search field; a row opens the group, "+" opens the real create modal. */
export function GroupsMobile() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { teams, isLoading, isError, refetch } = TeamsModule.Hooks.useMyTeams();
  const canManageTeams = usePermission(PERMISSIONS.TEAMS_CREATE);
  const [createOpen, setCreateOpen] = useState(false);
  const { input: search, setInput: setSearch } = useSearchParamState();

  const needle = search.trim().toLocaleLowerCase();
  const visible = useMemo(
    () => (needle ? teams.filter((team) => team.name.toLocaleLowerCase().includes(needle)) : teams),
    [teams, needle],
  );

  const renderBody = () => {
    if (isLoading) return <MobileSkeleton />;
    if (isError) return <MobileError onRetry={() => refetch()} />;
    if (teams.length === 0) {
      return (
        <MobileEmpty
          icon={<GraduationCap size={22} />}
          title={t('teams.empty_title', 'No groups yet')}
          description={t('teams.empty_desc', 'Create a group to organize your students.')}
        />
      );
    }
    if (visible.length === 0) {
      return (
        <MobileEmpty
          icon={<Search size={22} />}
          title={t('teams.no_teams_match')}
          description={t('try_adjusting_your_search_or_filters')}
          action={
            <button
              type="button"
              onClick={() => setSearch('')}
              className="h-10 rounded-md px-4 text-[14px] font-medium"
              style={{ background: M.card, border: `1px solid ${M.border}`, color: M.ink }}
            >
              {t('clear_search')}
            </button>
          }
        />
      );
    }
    return (
      <ScreenBody>
        <ListCard>
          {visible.map((team) => (
            <button
              key={team.id}
              type="button"
              onClick={() => navigate(`/app/teams/${team.id}`)}
              className="flex w-full items-center gap-3 px-3.5 py-3 text-left"
            >
              <GroupTile name={team.name} className="h-9 w-9 text-xs" />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[15px] font-medium leading-[18px]" style={{ color: M.ink }}>
                  {team.name}
                </span>
                {/* Real counts only — no avatar stack without per-member data behind it. */}
                <span className="mt-0.5 block truncate text-[12.5px] leading-4 tabular-nums" style={{ color: M.ink3 }}>
                  {t('teams.stat_members_count', { count: team.memberCount })}
                  {' · '}
                  {t('teams.stat_collections_count', { count: team.collectionCount })}
                </span>
              </span>
              <ChevronRight className="h-4 w-4 flex-shrink-0" style={{ color: M.chevron }} aria-hidden />
            </button>
          ))}
        </ListCard>
      </ScreenBody>
    );
  };

  return (
    <MobileScreen>
      <ScreenHeader
        title={t('nav.groups')}
        count={isLoading || isError ? undefined : teams.length}
        right={canManageTeams ? <HeaderPlus onClick={() => setCreateOpen(true)} label={t('groups_create')} /> : undefined}
      >
        <div className="pb-3 pt-3">
          <SearchInput value={search} onChange={setSearch} placeholder={t('groups_search_placeholder')} />
        </div>
      </ScreenHeader>

      {renderBody()}

      <CreateTeamModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSuccess={() => {
          setCreateOpen(false);
          refetch();
        }}
      />
    </MobileScreen>
  );
}

export default GroupsMobile;
